/**
 * ContactPanel — calm, brand-aligned contact information block.
 *
 * Renders the Alchamis Group HQ (address, email, LinkedIn) and the global
 * phone lines (USA, UK, Denmark, Morocco) with country flag emojis. Every
 * field is a real anchor — phone numbers `tel:`, email `mailto:`, LinkedIn
 * external — so the panel doubles as a discoverable contact card.
 *
 * Localized via marketing.contact.* keys. Country labels and the section
 * headings translate; the numbers and address itself stay in their native
 * format. Phone numbers render in `dir=ltr` even on RTL pages so digits
 * read correctly in Arabic.
 */
import { MapPin, Mail, Linkedin, Phone } from "lucide-react";
import { useI18n } from "../lib/i18n";

const LINES = [
  { flag: "\uD83C\uDDFA\uD83C\uDDF8", number: "+1 (973) 441-0708", country: "country_usa", tel: "+19734410708" },
  { flag: "\uD83C\uDDFA\uD83C\uDDF8", number: "+1 (908) 291-2217", country: "country_usa", tel: "+19082912217" },
  { flag: "\uD83C\uDDEC\uD83C\uDDE7", number: "+44 7552 233929", country: "country_uk", tel: "+447552233929" },
  { flag: "\uD83C\uDDE9\uD83C\uDDF0", number: "+45 31 32 10 29", country: "country_denmark", tel: "+4531321029" },
  { flag: "\uD83C\uDDF2\uD83C\uDDE6", number: "+212 663 179 040", country: "country_morocco", tel: "+212663179040" },
];

export default function ContactPanel({ variant = "card" }) {
  const { t } = useI18n();

  const card = (
    <div
      data-testid="contact-panel"
      className="rounded-3xl bg-white border border-[#EEF0F4] shadow-sm overflow-hidden"
    >
      <div className="grid lg:grid-cols-2">
        {/* Left — HQ + identifiers */}
        <div className="px-7 py-8 sm:px-10 sm:py-10 border-b lg:border-b-0 lg:border-e border-[#EEF0F4] bg-[#FAF6EE]/70">
          <div className="eyebrow text-[#1F3A2A]">
            {t("marketing.contact.eyebrow")}
          </div>
          <h3 className="font-serif text-3xl sm:text-4xl tracking-tight text-[#171717] mt-2">
            {t("marketing.contact.panel_title")}
          </h3>
          <p className="mt-3 text-sm text-[#525252] leading-relaxed max-w-md">
            {t("marketing.contact.panel_subtitle")}
          </p>

          <div className="mt-9 space-y-6">
            <div className="flex items-start gap-3">
              <span className="h-9 w-9 rounded-2xl bg-white shadow-sm flex items-center justify-center shrink-0">
                <MapPin size={14} strokeWidth={1.6} className="text-[#1F3A2A]" />
              </span>
              <div>
                <div className="eyebrow text-[#525252]">
                  {t("marketing.contact.hq_label")}
                </div>
                <address className="not-italic text-sm text-[#171717] mt-1 leading-relaxed">
                  {t("marketing.contact.hq_address_line1")}<br />
                  {t("marketing.contact.hq_address_line2")}
                </address>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <span className="h-9 w-9 rounded-2xl bg-white shadow-sm flex items-center justify-center shrink-0">
                <Mail size={14} strokeWidth={1.6} className="text-[#1F3A2A]" />
              </span>
              <div>
                <div className="eyebrow text-[#525252]">
                  {t("marketing.contact.email_label")}
                </div>
                <a
                  href={`mailto:${t("marketing.contact.email_address")}`}
                  data-testid="contact-email"
                  className="text-sm text-[#171717] mt-1 inline-block hover:text-[#1F3A2A] underline decoration-dotted underline-offset-4"
                  dir="ltr"
                >
                  {t("marketing.contact.email_address")}
                </a>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <span className="h-9 w-9 rounded-2xl bg-white shadow-sm flex items-center justify-center shrink-0">
                <Linkedin size={14} strokeWidth={1.6} className="text-[#1F3A2A]" />
              </span>
              <div>
                <div className="eyebrow text-[#525252]">
                  {t("marketing.contact.linkedin_label")}
                </div>
                <a
                  href="https://www.linkedin.com/company/alchamisgroup"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-linkedin"
                  className="text-sm text-[#171717] mt-1 inline-block hover:text-[#1F3A2A] underline decoration-dotted underline-offset-4"
                >
                  {t("marketing.contact.linkedin_handle")}
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Right — Global phone lines */}
        <div className="px-7 py-8 sm:px-10 sm:py-10">
          <div className="flex items-center gap-2">
            <Phone size={14} strokeWidth={1.6} className="text-[#1F3A2A]" />
            <span className="eyebrow text-[#1F3A2A]">
              {t("marketing.contact.global_lines_label")}
            </span>
          </div>
          <ul className="mt-6 space-y-4" data-testid="contact-lines">
            {LINES.map((line) => (
              <li key={line.tel}>
                <a
                  href={`tel:${line.tel}`}
                  data-testid={`contact-line-${line.tel}`}
                  className="group flex items-baseline justify-between gap-4 py-2 px-3 -mx-3 rounded-xl hover:bg-[#FAF6EE]/80 transition-colors"
                  dir="ltr"
                >
                  <div className="flex items-baseline gap-3 min-w-0">
                    <span className="text-lg leading-none" aria-hidden="true">
                      {line.flag}
                    </span>
                    <span className="font-mono text-base text-[#171717] tabular-nums">
                      {line.number}
                    </span>
                  </div>
                  <span className="eyebrow text-[#525252] group-hover:text-[#1F3A2A] transition-colors shrink-0">
                    {t(`marketing.contact.${line.country}`)}
                  </span>
                </a>
              </li>
            ))}
          </ul>
          <p className="mt-7 text-xs text-[#525252] leading-relaxed">
            {t("marketing.contact.or_use_form_below")}
          </p>
        </div>
      </div>
    </div>
  );

  if (variant === "footer") {
    // Compact variant for the marketing footer (just HQ + email).
    return (
      <div data-testid="contact-panel-footer" className="space-y-3 text-sm">
        <div className="eyebrow">{t("marketing.contact.eyebrow")}</div>
        <address className="not-italic text-[#6B7280] leading-relaxed">
          {t("marketing.contact.hq_address_line1")}<br />
          {t("marketing.contact.hq_address_line2")}
        </address>
        <a
          href={`mailto:${t("marketing.contact.email_address")}`}
          className="text-[#6B7280] hover:text-[#1F3A2A] underline decoration-dotted underline-offset-4 inline-block"
          dir="ltr"
        >
          {t("marketing.contact.email_address")}
        </a>
      </div>
    );
  }
  return card;
}
